import { useMemo, useState } from 'react';
import { RoseChart, RoseLegend, STATUS_LABELS, type RoseFamily } from '../components/RoseChart';
import { DeleteButton, ErrorBanner, Field, Loading, Modal } from '../components/ui';
import { api } from '../lib/api';
import { downloadCsv, fileStamp, slugForFile } from '../lib/download';
import { formatDate, humanise } from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  ComplianceAssessment,
  ComplianceEvidence,
  ComplianceRequirement,
  ResultStatus,
} from '../lib/types';

const STATUS_ORDER: ResultStatus[] = ['met', 'partial', 'not_met', 'not_applicable'];

const EVIDENCE_TYPES = [
  'policy',
  'screenshot',
  'config',
  'report',
  'ticket',
  'attestation',
  'other',
] as const;

/** The tags a framework hangs on a requirement, in the order they read best. */
function metaTags(meta: string | null): string[] {
  if (!meta) return [];
  try {
    const parsed = JSON.parse(meta) as Record<string, string>;
    const tags: string[] = [];
    for (const key of ['asset_type', 'security_function', 'type', 'rule', 'category', 'maturity', 'tier']) {
      if (parsed[key]) tags.push(parsed[key]);
    }
    if (parsed.nist) tags.push(`NIST ${parsed.nist}`);
    /* The GLBA / NIST 800-171 risk assessment (seed 015): the Safeguards Rule
       citation, then the 800-171 Rev. 3 families the question aligns to. */
    if (parsed.glba) tags.push(`GLBA ${parsed.glba}`);
    if (parsed.nist_alignment) tags.push(`NIST 800-171: ${parsed.nist_alignment}`);
    if (parsed.far) tags.push(`FAR ${parsed.far}`);
    if (parsed.enhanced) tags.push('Enhanced');
    return tags;
  } catch {
    return [];
  }
}

/** "What goes wrong without this" — the framework's own words, where it has any. */
function metaRisk(meta: string | null): string | null {
  if (!meta) return null;
  try {
    return (JSON.parse(meta) as { risk?: string }).risk ?? null;
  } catch {
    return null;
  }
}

/* --- a framework's method, and risk scoring (migration 157) ---------------- */

interface ScalePoint {
  value: number;
  label: string;
  detail: string;
}
interface RatingBand {
  key: 'low' | 'moderate' | 'high' | 'critical';
  label: string;
  min: number;
  max: number;
  detail: string;
}
interface Guidance {
  riskScoring?: boolean;
  purpose?: string;
  steps?: { title: string; detail: string }[];
  statuses?: { key: string; label: string; detail: string }[];
  likelihood?: ScalePoint[];
  impact?: ScalePoint[];
  ratings?: RatingBand[];
  treatments?: { key: string; label: string; detail: string }[];
  disclaimer?: string;
}

function parseGuidance(json: string | null | undefined): Guidance | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as Guidance;
  } catch {
    return null;
  }
}

/** How to test it, what each status requires, what to collect — where the framework says. */
function metaDetail(meta: string | null): {
  test: string[];
  criteria: Partial<Record<'met' | 'partial' | 'not_met', string>>;
  evidence: string[];
} {
  try {
    const m = meta ? (JSON.parse(meta) as Record<string, unknown>) : {};
    return {
      test: Array.isArray(m.test) ? (m.test as string[]) : [],
      criteria: (m.criteria as Record<string, string>) ?? {},
      evidence: Array.isArray(m.evidence) ? (m.evidence as string[]) : [],
    };
  } catch {
    return { test: [], criteria: {}, evidence: [] };
  }
}

const riskScore = (r: Pick<ComplianceRequirement, 'Likelihood' | 'Impact'>) =>
  r.Likelihood && r.Impact ? r.Likelihood * r.Impact : null;
const ratingOf = (score: number | null, bands: RatingBand[] | undefined) =>
  score === null ? undefined : (bands ?? []).find((b) => score >= b.min && score <= b.max);
const isGap = (r: ComplianceRequirement) => r.Status === 'partial' || r.Status === 'not_met';
const dateOnly = (v: string | null) => (v ? v.slice(0, 10) : '');

/** The risk fields, as the API names them. */
const RISK_KEYS = {
  Likelihood: 'likelihood',
  Impact: 'impact',
  Treatment: 'treatment',
  Finding: 'finding',
  Remediation: 'remediation',
  RemediationOwner: 'remediationOwner',
  TargetDate: 'targetDate',
} as const;
type RiskPatch = Partial<Pick<ComplianceRequirement, keyof typeof RISK_KEYS>>;

/**
 * Working an assessment.
 *
 * Score = met ÷ applicable, where applicable excludes anything marked not
 * applicable. That is the Compliance Tracker's definition, kept deliberately
 * identical: two apps reporting different percentages for the same client is
 * worse than either number being slightly off.
 */
export function AssessmentWorkspace({
  assessmentId,
  onBack,
  onChanged,
}: {
  assessmentId: number;
  onBack: () => void;
  onChanged: () => void;
}) {
  const assessment = useResource<ComplianceAssessment>(`/compliance/assessments/${assessmentId}`);
  const [openFamily, setOpenFamily] = useState<number | null>(null);
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [evidenceFor, setEvidenceFor] = useState<ComplianceRequirement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const data = assessment.data;
  const guidance = useMemo(() => parseGuidance(data?.Guidance), [data?.Guidance]);
  const scoring = guidance?.riskScoring === true;

  /* Tallies are computed here rather than fetched: the page already holds
     every requirement, so a click can move the score without a round trip. */
  const view = useMemo(() => {
    if (!data) return null;

    const byFamily = new Map<number, ComplianceRequirement[]>();
    for (const r of data.requirements) {
      const list = byFamily.get(r.FamilyId);
      if (list) list.push(r);
      else byFamily.set(r.FamilyId, [r]);
    }

    const families = data.families.map((f) => {
      const items = byFamily.get(f.FamilyId) ?? [];
      const tally: Record<ResultStatus, number> = {
        met: 0,
        partial: 0,
        not_met: 0,
        not_applicable: 0,
        not_assessed: 0,
      };
      for (const r of items) tally[r.Status] += 1;
      const applicable = items.length - tally.not_applicable;
      return {
        ...f,
        items,
        tally,
        applicable,
        pct: applicable > 0 ? Math.round((tally.met / applicable) * 100) : null,
      };
    });

    const total: Record<ResultStatus, number> = {
      met: 0,
      partial: 0,
      not_met: 0,
      not_applicable: 0,
      not_assessed: 0,
    };
    for (const f of families) {
      for (const key of Object.keys(total) as ResultStatus[]) total[key] += f.tally[key];
    }
    const applicable = data.requirements.length - total.not_applicable;

    return {
      families,
      total,
      applicable,
      inScope: data.requirements.length,
      overallPct: applicable > 0 ? Math.round((total.met / applicable) * 100) : null,
    };
  }, [data]);

  if (assessment.loading) return <Loading label="Loading the assessment" />;
  if (assessment.error) return <ErrorBanner message={assessment.error} />;
  if (!data || !view) return null;

  const subject = data.ClientName ?? 'Our organisation';

  const setStatus = (requirement: ComplianceRequirement, status: ResultStatus) => {
    /* Clicking the status a requirement already has clears it back to not
       assessed — otherwise a mis-click can never be undone. */
    const next = requirement.Status === status ? 'not_assessed' : status;

    /* Applied locally first: working a 153-safeguard framework is a rhythm,
       and a spinner between every click breaks it. The reload afterwards is
       what makes the assessed-on stamp and evidence counts correct. */
    assessment.set((current) => ({
      ...current,
      requirements: current.requirements.map((r) =>
        r.RequirementId === requirement.RequirementId ? { ...r, Status: next } : r,
      ),
    }));

    void api
      .put(`/compliance/assessments/${assessmentId}/requirements/${requirement.RequirementId}`, {
        status: next,
      })
      .then(onChanged)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not save that');
        assessment.reload();
      });
  };

  /* Applied locally, then saved — the same rhythm as a status click. */
  const setRisk = (requirement: ComplianceRequirement, patch: RiskPatch) => {
    assessment.set((current) => ({
      ...current,
      requirements: current.requirements.map((r) =>
        r.RequirementId === requirement.RequirementId ? { ...r, ...patch } : r,
      ),
    }));
    const body = Object.fromEntries(
      Object.entries(patch).map(([k, v]) => [RISK_KEYS[k as keyof typeof RISK_KEYS], v ?? null]),
    );
    void api
      .put(`/compliance/assessments/${assessmentId}/requirements/${requirement.RequirementId}`, body)
      .then(onChanged)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not save that');
        assessment.reload();
      });
  };

  const saveNotes = (requirement: ComplianceRequirement, notes: string) => {
    void api
      .put(`/compliance/assessments/${assessmentId}/requirements/${requirement.RequirementId}`, {
        notes: notes.trim() || null,
      })
      .then(() => assessment.reload())
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not save'));
  };

  const roseFamilies: RoseFamily[] = view.families.map((f) => ({
    FamilyId: f.FamilyId,
    Code: f.Code,
    Title: f.Title,
    cells: f.items.map((r) => ({
      RequirementId: r.RequirementId,
      Code: r.Code,
      Title: r.Title,
      Status: r.Status,
    })),
  }));

  return (
    <>
      <div className="row-between wrap mb-4 no-print">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
          ← All assessments
        </button>
        <div className="row wrap">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={onlyGaps}
              onChange={(e) => setOnlyGaps(e.target.checked)}
            />
            Only what is not met yet
          </label>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={data.ScopeLevelKey}
            onChange={(e) =>
              void api
                .put(`/compliance/assessments/${assessmentId}`, { scopeLevelKey: e.target.value })
                .then(() => {
                  assessment.reload();
                  onChanged();
                })
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : 'Could not change the scope'),
                )
            }
          >
            {data.levels.map((l) => (
              <option key={l.LevelKey} value={l.LevelKey}>
                {l.Label}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.print()}>
            Print report
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <header className="page-head">
        <span className="eyebrow">{subject}</span>
        <h1>{data.FrameworkName}</h1>
        <p className="lede">
          {data.ScopeLevelLabel ?? data.ScopeLevelKey} · {view.inScope} requirements in scope ·{' '}
          {data.OwnerName ? `owned by ${data.OwnerName}` : 'no owner named'}
          {data.CompletedOn && ` · completed ${formatDate(data.CompletedOn)}`}
        </p>
      </header>

      {guidance && <MethodPanel guidance={guidance} started={view.total.not_assessed < view.inScope} />}

      {scoring && (
        <RiskRegister
          requirements={data.requirements}
          bands={guidance?.ratings}
          title={`${subject} — ${data.FrameworkName}`}
          onOpen={(r) => setOpenFamily(r.FamilyId)}
        />
      )}

      {/* Score on the left, worklist on the right. The chart and the per-family
          bars belong together — the bars are the exact read of what the chart
          shows — and keeping them beside the requirements means you can see
          the effect of a status change without scrolling back up to find it. */}
      <div className="assessment-layout">
        <aside className="assessment-side">
          <section className="card">
            <div className="card-body">
              {/* Chart and legend are one object on paper: the legend is
                  unreadable on a page that does not carry the chart, so they
                  travel together and land on the first page under the head. */}
              <div className="rose-block">
                <RoseChart
                  families={roseFamilies}
                  overallPct={view.overallPct}
                  onSelectFamily={(id) => setOpenFamily(id)}
                />

                <RoseLegend tally={view.total} />
              </div>

              <div className="mt-6">
                <span className="eyebrow">By family</span>
                <div className="family-bars mt-4">
                  {view.families.map((f) => (
                    <button
                      key={f.FamilyId}
                      type="button"
                      className={`family-bar${openFamily === f.FamilyId ? ' on' : ''}`}
                      onClick={() =>
                        setOpenFamily((c) => (c === f.FamilyId ? null : f.FamilyId))
                      }
                    >
                      <span className="small strong grow">
                        <span className="mono xs muted">{f.Code}</span> {f.Title}
                      </span>
                      <span className="meter" style={{ width: 72 }}>
                        <span
                          className={f.pct !== null && f.pct < 50 ? 'low' : ''}
                          style={{ width: `${f.pct ?? 0}%` }}
                        />
                      </span>
                      <span className="mono xs" style={{ width: 42, textAlign: 'right' }}>
                        {f.pct === null ? '—' : `${f.pct}%`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </aside>

        <div className="assessment-work stack">
        {view.families.map((family) => {
          const items = onlyGaps
            ? family.items.filter((r) => r.Status === 'not_met' || r.Status === 'not_assessed')
            : family.items;
          if (onlyGaps && items.length === 0) return null;

          const open = openFamily === family.FamilyId;

          return (
            <section key={family.FamilyId} className="card">
              <button
                type="button"
                className="card-head as-toggle"
                onClick={() => setOpenFamily(open ? null : family.FamilyId)}
              >
                <div className="grow">
                  <span className="eyebrow">
                    {family.Code} · {family.items.length}{' '}
                    {family.items.length === 1 ? 'requirement' : 'requirements'}
                  </span>
                  <h2>{family.Title}</h2>
                  {family.Description && <p className="sub">{family.Description}</p>}
                </div>
                <div className="row">
                  <span className="mono strong">{family.pct === null ? '—' : `${family.pct}%`}</span>
                  <span className="xs muted">{open ? '▲' : '▼'}</span>
                </div>
              </button>

              {/* Rendered whether or not the family is open, and hidden with
                  CSS rather than unmounted — a printed report has to carry
                  every family, and a collapsed one that is not in the DOM
                  cannot be revealed by a print stylesheet. */}
              <div className={`requirement-list${open ? '' : ' collapsed'}`}>
                {items.map((r) => (
                  <RequirementRow
                    key={r.RequirementId}
                    requirement={r}
                    evidence={data.evidence.filter((e) => e.RequirementId === r.RequirementId)}
                    onStatus={(status) => setStatus(r, status)}
                    onNotes={(notes) => saveNotes(r, notes)}
                    guidance={scoring ? guidance : null}
                    onRisk={(patch) => setRisk(r, patch)}
                    onEvidence={() => setEvidenceFor(r)}
                  />
                ))}
              </div>
            </section>
          );
        })}
        </div>
      </div>

      {evidenceFor && (
        <EvidenceEditor
          assessmentId={assessmentId}
          requirement={evidenceFor}
          evidence={data.evidence.filter((e) => e.RequirementId === evidenceFor.RequirementId)}
          onClose={() => setEvidenceFor(null)}
          onChanged={() => assessment.reload()}
        />
      )}
    </>
  );
}

function RequirementRow({
  requirement: r,
  evidence,
  onStatus,
  onNotes,
  onEvidence,
  guidance,
  onRisk,
}: {
  requirement: ComplianceRequirement;
  evidence: ComplianceEvidence[];
  onStatus: (status: ResultStatus) => void;
  onNotes: (notes: string) => void;
  onEvidence: () => void;
  /** Present when the framework scores risk. */
  guidance: Guidance | null;
  onRisk: (patch: RiskPatch) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(r.Notes ?? '');
  const tags = metaTags(r.Meta);
  const risk = metaRisk(r.Meta);
  const detail = metaDetail(r.Meta);
  const score = riskScore(r);
  const band = ratingOf(score, guidance?.ratings);

  return (
    <div className={`requirement ${r.Status.replace('_', '-')}`}>
      <div className="requirement-main">
        <div className="grow">
          <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
            <span className="mono xs muted">{r.Code}</span>
            <span className="strong small">{r.Title}</span>
          </div>

          {(tags.length > 0 || band || (guidance && isGap(r) && score === null)) && (
            <div className="row wrap mt-2" style={{ gap: 4 }}>
              {band && (
                <span className={`risk-badge ${band.key}`} title={`Likelihood ${r.Likelihood} × impact ${r.Impact}`}>
                  {band.label} risk · {score}
                </span>
              )}
              {guidance && isGap(r) && score === null && <span className="risk-badge unscored">Risk not scored</span>}
              {tags.map((t) => (
                <span key={t} className="badge badge-neutral">
                  {t}
                </span>
              ))}
            </div>
          )}

          <button
            type="button"
            className="btn btn-ghost btn-sm mt-2"
            onClick={() => setExpanded((c) => !c)}
          >
            {expanded ? 'Less' : 'Detail'}
          </button>

          {expanded && (
            <div className="mt-4">
              {r.Description && <p className="small muted">{r.Description}</p>}

              {detail.test.length > 0 && (
                <div className="ra-block mt-4">
                  <span className="eyebrow">How to test</span>
                  <ol className="ra-list small mt-2">
                    {detail.test.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ol>
                </div>
              )}

              {(detail.criteria.met || detail.criteria.partial || detail.criteria.not_met) && (
                <div className="ra-block mt-4">
                  <span className="eyebrow">Evaluation criteria</span>
                  <div className="ra-criteria mt-2">
                    {(['met', 'partial', 'not_met'] as const).map(
                      (k) =>
                        detail.criteria[k] && (
                          <div key={k} className={`ra-criterion ${k.replace('_', '-')}${r.Status === k ? ' on' : ''}`}>
                            <span className="xs strong">{k === 'met' ? 'Met' : k === 'partial' ? 'Partial' : 'Not met'}</span>
                            <span className="small">{detail.criteria[k]}</span>
                          </div>
                        ),
                    )}
                  </div>
                </div>
              )}

              {detail.evidence.length > 0 && (
                <div className="ra-block mt-4">
                  <span className="eyebrow">Evidence to collect</span>
                  <ul className="ra-list small mt-2">
                    {detail.evidence.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}

              {risk && (
                <div className="risk-note mt-4">
                  <span className="eyebrow">Without it</span>
                  <p className="small mt-2">{risk}</p>
                </div>
              )}

              {guidance && <RiskEditor requirement={r} guidance={guidance} onRisk={onRisk} />}
              <div className="mt-4">
                <Field label="Assessment notes">
                  <textarea
                    className="textarea"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => {
                      if ((r.Notes ?? '') !== notes) onNotes(notes);
                    }}
                    placeholder="What was checked, and what was found."
                  />
                </Field>
              </div>
            </div>
          )}

          {!expanded && r.Notes && <p className="xs muted mt-2">{r.Notes}</p>}
        </div>

        <div className="requirement-side">
          <div className="status-group" role="group" aria-label={`Status for ${r.Code}`}>
            {STATUS_ORDER.map((status) => (
              <button
                key={status}
                type="button"
                className={`status-btn ${status.replace('_', '-')}${r.Status === status ? ' on' : ''}`}
                aria-pressed={r.Status === status}
                title={STATUS_LABELS[status]}
                onClick={() => onStatus(status)}
              >
                {status === 'met'
                  ? 'Met'
                  : status === 'partial'
                    ? 'Partial'
                    : status === 'not_met'
                      ? 'Not met'
                      : 'N/A'}
              </button>
            ))}
          </div>

          <button type="button" className="btn btn-ghost btn-sm" onClick={onEvidence}>
            Evidence{evidence.length > 0 ? ` (${evidence.length})` : ''}
          </button>

          {r.AssessedOn && (
            <span className="xs subtle">
              {formatDate(r.AssessedOn)}
              {r.AssessedByName ? ` · ${r.AssessedByName}` : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function EvidenceEditor({
  assessmentId,
  requirement,
  evidence,
  onClose,
  onChanged,
}: {
  assessmentId: number;
  requirement: ComplianceRequirement;
  evidence: ComplianceEvidence[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [form, setForm] = useState({ label: '', url: '', evidenceType: 'policy', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(
        `/compliance/assessments/${assessmentId}/requirements/${requirement.RequirementId}/evidence`,
        {
          label: form.label.trim(),
          url: form.url.trim() || null,
          evidenceType: form.evidenceType,
          notes: form.notes.trim() || null,
        },
      );
      setForm({ label: '', url: '', evidenceType: 'policy', notes: '' });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    }
    setSaving(false);
  };

  return (
    <Modal
      title={`Evidence — ${requirement.Code}`}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      {error && <ErrorBanner message={error} />}

      <p className="xs muted">
        Evidence is recorded as a link to where the artefact actually lives — SharePoint, a ticket,
        the Compliance Tracker. Uploading it here would put the only copy on an App Service disk
        that does not survive a restart.
      </p>

      {evidence.length > 0 && (
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          {evidence.map((e) => (
            <div key={e.EvidenceId} className="mini-row">
              <div className="grow">
                <div className="small strong">
                  {e.Url ? (
                    <a href={e.Url} target="_blank" rel="noopener noreferrer">
                      {e.Label} ↗
                    </a>
                  ) : (
                    e.Label
                  )}
                </div>
                <div className="xs muted">
                  {humanise(e.EvidenceType)}
                  {e.AddedByName ? ` · ${e.AddedByName}` : ''}
                  {e.Notes ? ` · ${e.Notes}` : ''}
                </div>
              </div>
              <DeleteButton
                what={`the evidence "${e.Label}"`}
                label="Remove"
                onConfirm={() =>
                  void api.del(`/compliance/evidence/${e.EvidenceId}`).then(onChanged)
                }
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <div className="form-grid">
          <Field label="What it is">
            <input
              className="input"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Asset inventory export, March"
            />
          </Field>
          <Field label="Type">
            <select
              className="select"
              value={form.evidenceType}
              onChange={(e) => setForm({ ...form, evidenceType: e.target.value })}
            >
              {EVIDENCE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {humanise(t)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Link">
            <input
              className="input"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://…"
            />
          </Field>
        </div>

        <button
          type="button"
          className="btn btn-primary btn-sm mt-4"
          disabled={saving || !form.label.trim()}
          onClick={() => void add()}
        >
          {saving ? 'Adding…' : 'Add evidence'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * How to run the assessment: the framework's method, from its Guidance.
 * Open until the first question is rated, then folded away — it is the
 * briefing, not the work.
 */
function MethodPanel({ guidance, started }: { guidance: Guidance; started: boolean }) {
  const [open, setOpen] = useState(!started);
  const bands = guidance.ratings ?? [];
  const L = guidance.likelihood ?? [];
  const I = guidance.impact ?? [];
  return (
    <section className="card mb-4 no-print">
      <button type="button" className="card-head as-toggle" onClick={() => setOpen((o) => !o)}>
        <div className="grow">
          <span className="eyebrow">Method</span>
          <h2>How to run this assessment</h2>
          {!open && <p className="sub">Steps, status definitions, and the likelihood and impact scales used to score risk.</p>}
        </div>
        <span className="xs muted">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="card-body ra-method">
          {guidance.purpose && <p className="small">{guidance.purpose}</p>}

          {guidance.steps && (
            <>
              <h3 className="mt-4">Steps</h3>
              <ol className="ra-steps">
                {guidance.steps.map((st) => (
                  <li key={st.title}>
                    <strong>{st.title}.</strong> <span className="small">{st.detail}</span>
                  </li>
                ))}
              </ol>
            </>
          )}

          {guidance.statuses && (
            <>
              <h3 className="mt-4">Statuses</h3>
              <table className="table ra-table">
                <tbody>
                  {guidance.statuses.map((st) => (
                    <tr key={st.key}>
                      <td className="strong" style={{ width: 200 }}>{st.label}</td>
                      <td className="small">{st.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {guidance.riskScoring && L.length > 0 && I.length > 0 && (
            <>
              <h3 className="mt-4">Scoring risk</h3>
              <p className="small muted">
                Risk score = likelihood × impact, each rated 1–5. Score the risk as it stands today, with the controls actually in place.
              </p>
              <div className="ra-scales">
                <div>
                  <h4>Likelihood</h4>
                  <table className="table ra-table">
                    <tbody>
                      {L.map((p) => (
                        <tr key={p.value}>
                          <td className="mono strong" style={{ width: 28 }}>{p.value}</td>
                          <td className="strong" style={{ width: 80 }}>{p.label}</td>
                          <td className="small">{p.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h4>Impact</h4>
                  <table className="table ra-table">
                    <tbody>
                      {I.map((p) => (
                        <tr key={p.value}>
                          <td className="mono strong" style={{ width: 28 }}>{p.value}</td>
                          <td className="strong" style={{ width: 80 }}>{p.label}</td>
                          <td className="small">{p.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="ra-scales mt-4">
                <div>
                  <h4>Risk matrix</h4>
                  <table className="ra-matrix" aria-label="Risk matrix: likelihood by impact">
                    <thead>
                      <tr>
                        <th className="xs muted">Likelihood ↓ / Impact →</th>
                        {I.map((p) => (
                          <th key={p.value} className="xs">
                            {p.value} {p.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...L].reverse().map((l) => (
                        <tr key={l.value}>
                          <th className="xs">
                            {l.value} {l.label}
                          </th>
                          {I.map((i) => {
                            const sc = l.value * i.value;
                            const b = ratingOf(sc, bands);
                            return (
                              <td key={i.value} className={`ra-cell ${b?.key ?? ''}`}>
                                {sc}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h4>Ratings and what they require</h4>
                  <table className="table ra-table">
                    <tbody>
                      {bands.map((b) => (
                        <tr key={b.key}>
                          <td style={{ width: 110 }}>
                            <span className={`risk-badge ${b.key}`}>{b.label}</span>
                          </td>
                          <td className="mono xs" style={{ width: 54 }}>
                            {b.min}–{b.max}
                          </td>
                          <td className="small">{b.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {guidance.treatments && (
                    <>
                      <h4 className="mt-4">Treatments</h4>
                      <table className="table ra-table">
                        <tbody>
                          {guidance.treatments.map((t) => (
                            <tr key={t.key}>
                              <td className="strong" style={{ width: 110 }}>{t.label}</td>
                              <td className="small">{t.detail}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {guidance.disclaimer && <p className="xs muted mt-4">{guidance.disclaimer}</p>}
        </div>
      )}
    </section>
  );
}

/** Likelihood, impact, the finding and what is being done about it — on one requirement. */
function RiskEditor({
  requirement: r,
  guidance,
  onRisk,
}: {
  requirement: ComplianceRequirement;
  guidance: Guidance;
  onRisk: (patch: RiskPatch) => void;
}) {
  const [finding, setFinding] = useState(r.Finding ?? '');
  const [remediation, setRemediation] = useState(r.Remediation ?? '');
  const [owner, setOwner] = useState(r.RemediationOwner ?? '');
  const score = riskScore(r);
  const band = ratingOf(score, guidance.ratings);
  const blurSave = (key: 'Finding' | 'Remediation' | 'RemediationOwner', value: string) => {
    if ((r[key] ?? '') !== value) onRisk({ [key]: value.trim() || null });
  };
  return (
    <div className="ra-risk mt-4">
      <div className="row-between wrap">
        <span className="eyebrow">Risk</span>
        {band ? (
          <span className={`risk-badge ${band.key}`}>
            {band.label} · {r.Likelihood} × {r.Impact} = {score}
          </span>
        ) : (
          <span className="xs muted">
            {isGap(r) ? 'Rate likelihood and impact for this gap.' : 'Optional for a met requirement with a residual concern.'}
          </span>
        )}
      </div>
      <div className="ra-risk-grid mt-2">
        <Field label="Likelihood">
          <select
            className="select"
            value={r.Likelihood ?? ''}
            onChange={(e) => onRisk({ Likelihood: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">—</option>
            {(guidance.likelihood ?? []).map((p) => (
              <option key={p.value} value={p.value} title={p.detail}>
                {p.value} — {p.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Impact">
          <select
            className="select"
            value={r.Impact ?? ''}
            onChange={(e) => onRisk({ Impact: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">—</option>
            {(guidance.impact ?? []).map((p) => (
              <option key={p.value} value={p.value} title={p.detail}>
                {p.value} — {p.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Treatment">
          <select
            className="select"
            value={r.Treatment ?? ''}
            onChange={(e) => onRisk({ Treatment: (e.target.value || null) as ComplianceRequirement['Treatment'] })}
          >
            <option value="">—</option>
            {(guidance.treatments ?? []).map((t) => (
              <option key={t.key} value={t.key} title={t.detail}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Owner">
          <input className="input" value={owner} onChange={(e) => setOwner(e.target.value)} onBlur={() => blurSave('RemediationOwner', owner)} placeholder="Who fixes it" />
        </Field>
        <Field label="Target date">
          <input
            className="input"
            type="date"
            value={dateOnly(r.TargetDate)}
            onChange={(e) => onRisk({ TargetDate: e.target.value || null })}
          />
        </Field>
      </div>
      {band && <p className="xs muted mt-2">{band.detail}</p>}
      <div className="form-grid mt-2">
        <Field label="Finding">
          <textarea
            className="textarea"
            rows={3}
            value={finding}
            onChange={(e) => setFinding(e.target.value)}
            onBlur={() => blurSave('Finding', finding)}
            placeholder="What is wrong, what it should be, why, and what it exposes."
          />
        </Field>
        <Field label="Remediation">
          <textarea
            className="textarea"
            rows={3}
            value={remediation}
            onChange={(e) => setRemediation(e.target.value)}
            onBlur={() => blurSave('Remediation', remediation)}
            placeholder={r.Treatment === 'accept' ? 'Why the risk is accepted, and who approved it.' : 'What will be done to close the gap.'}
          />
        </Field>
      </div>
    </div>
  );
}

/**
 * The risk register: every scored risk and every unscored gap, worst first.
 * What the Qualified Individual reports from, and what remediation is tracked
 * against. Printed with the report.
 */
function RiskRegister({
  requirements,
  bands,
  title,
  onOpen,
}: {
  requirements: ComplianceRequirement[];
  bands: RatingBand[] | undefined;
  title: string;
  onOpen: (r: ComplianceRequirement) => void;
}) {
  const rows = requirements
    .filter((r) => riskScore(r) !== null || isGap(r))
    .map((r) => ({ r, score: riskScore(r), band: ratingOf(riskScore(r), bands) }))
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1) || a.r.SortOrder - b.r.SortOrder);
  const count = (k: RatingBand['key']) => rows.filter((x) => x.band?.key === k).length;
  const unscored = rows.filter((x) => x.score === null).length;
  const overdue = rows.filter((x) => x.r.TargetDate && dateOnly(x.r.TargetDate) < new Date().toISOString().slice(0, 10)).length;

  const exportCsv = () =>
    downloadCsv(`${slugForFile(title)}_risk_register_${fileStamp()}.csv`, [
      ['ID', 'Question', 'Status', 'Likelihood', 'Impact', 'Risk score', 'Rating', 'Finding', 'Treatment', 'Remediation', 'Owner', 'Target date', 'Notes'],
      ...rows.map(({ r, score, band }) => [
        r.Code,
        r.Title,
        STATUS_LABELS[r.Status],
        r.Likelihood ?? '',
        r.Impact ?? '',
        score ?? '',
        band?.label ?? '',
        r.Finding ?? '',
        r.Treatment ? humanise(r.Treatment) : '',
        r.Remediation ?? '',
        r.RemediationOwner ?? '',
        dateOnly(r.TargetDate),
        r.Notes ?? '',
      ]),
    ]);

  return (
    <section className="card mb-4 ra-register">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Risk register</span>
          <h2>Risks and gaps, worst first</h2>
          <p className="sub">
            Every question rated Partial or Not met, and anything with a risk score. Rate likelihood and impact on the question to score it.
          </p>
        </div>
        <button type="button" className="btn btn-secondary btn-sm no-print" disabled={rows.length === 0} onClick={exportCsv}>
          Export CSV
        </button>
      </div>
      <div className="card-body">
        <div className="ra-counts">
          {(['critical', 'high', 'moderate', 'low'] as const).map((k) => (
            <div key={k} className={`ra-count ${k}`}>
              <span className="figure">{count(k)}</span>
              <span className="xs">{bands?.find((b) => b.key === k)?.label ?? k}</span>
            </div>
          ))}
          <div className="ra-count unscored">
            <span className="figure">{unscored}</span>
            <span className="xs">Gaps not scored</span>
          </div>
          <div className="ra-count">
            <span className="figure">{overdue}</span>
            <span className="xs">Past target date</span>
          </div>
        </div>
        {rows.length === 0 ? (
          <p className="small muted mt-4">No gaps recorded yet. Rate questions Partial or Not met, then score their risk.</p>
        ) : (
          <div className="table-wrap mt-4">
            <table className="table ra-register-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th>Question and finding</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th className="num" style={{ width: 70 }}>L × I</th>
                  <th style={{ width: 120 }}>Rating</th>
                  <th style={{ width: 110 }}>Treatment</th>
                  <th style={{ width: 140 }}>Owner</th>
                  <th style={{ width: 100 }}>Target</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ r, score, band }) => (
                  <tr key={r.RequirementId}>
                    <td className="mono xs">
                      <button type="button" className="link-button" onClick={() => onOpen(r)}>
                        {r.Code}
                      </button>
                    </td>
                    <td>
                      <div className="small">{r.Title}</div>
                      {r.Finding && <div className="xs muted mt-1">{r.Finding}</div>}
                      {r.Remediation && <div className="xs mt-1"><strong>Remediation:</strong> {r.Remediation}</div>}
                    </td>
                    <td className="xs">{STATUS_LABELS[r.Status]}</td>
                    <td className="num mono xs">{score === null ? '—' : `${r.Likelihood} × ${r.Impact} = ${score}`}</td>
                    <td>{band ? <span className={`risk-badge ${band.key}`}>{band.label}</span> : <span className="risk-badge unscored">Not scored</span>}</td>
                    <td className="xs">{r.Treatment ? humanise(r.Treatment) : '—'}</td>
                    <td className="xs">{r.RemediationOwner ?? '—'}</td>
                    <td className="xs">{r.TargetDate ? formatDate(dateOnly(r.TargetDate)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
